/**
 * ============================================================================
 * KbBacklinksPanel — Seção "Referenciado em" com paginação. Consulta
 *                    kb_page_links via RPC (nunca varre JSON).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileText, Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useKbBacklinks } from "@/hooks/kb/useKbBacklinks";

interface Props {
  pageId?: string | null;
  pageSize?: number;
  /** Compacto (para drawer). */
  compact?: boolean;
}

export function KbBacklinksPanel({ pageId, pageSize = 10, compact }: Props) {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useKbBacklinks(pageId, page, pageSize);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Carregando referências…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-2">
        Nenhum artigo referencia esta página ainda.
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      {rows.map((r) => (
        <RouterLink
          key={r.source_page_id + (r.anchor ?? "")}
          to={`/tenant/kb/${r.space_id}/pages/${r.source_page_id}${r.anchor ? `#${r.anchor}` : ""}`}
          className="flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs hover:bg-accent transition-colors"
        >
          <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{r.source_title}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {r.space_name}
              {r.collection_name ? ` › ${r.collection_name}` : ""}
              {r.anchor ? ` · #${r.anchor}` : ""}
            </div>
          </div>
          <Badge variant="outline" className="text-[9px] shrink-0">
            {r.source_status}
          </Badge>
        </RouterLink>
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1 text-[10px] text-muted-foreground">
          <span>{total} referência{total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span>{page + 1}/{totalPages}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
